/**
 * Input sanitization utilities.
 * Must exist before any external data is processed.
 */

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
};

const HTML_ESCAPE_RE = /[&<>"'`]/g;

/**
 * Strip HTML tags and decode entities from an HTML string.
 * Uses DOMParser for safe parsing (no innerHTML assignment).
 */
export function stripHtml(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent?.trim() ?? '';
  } catch {
    // Fallback: regex strip for environments without DOMParser
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim();
  }
}

/** Escape HTML special characters to prevent XSS. */
export function escapeHtml(str: string): string {
  return str.replace(HTML_ESCAPE_RE, ch => HTML_ESCAPE_MAP[ch] ?? ch);
}

/** Escape a string for safe use in HTML attributes. */
export function escapeAttr(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript|blob|filesystem):/i;

/**
 * Sanitize a URL — blocks javascript:, data:, and vbscript: URIs.
 * Handles encoded variants (percent-encoding, HTML entities) that bypass naive checks.
 * Returns empty string for blocked URLs.
 */
export function sanitizeUrl(url: string): string {
  const trimmed = url.trim();

  // Decode percent-encoding before checking protocols
  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    // If decoding fails, still check the raw string
  }
  // Decode HTML numeric entities (&#NNN; and &#xHH;)
  decoded = decoded.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)));

  // Strip whitespace and control characters that can bypass protocol checks
  const normalized = decoded.replace(/[\s\x00-\x1f]/g, '').toLowerCase();

  if (DANGEROUS_PROTOCOLS.test(normalized)) {
    return '';
  }
  return trimmed;
}
